import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useNavigate } from 'react-router-dom';
import { SEC1_COLUMNS, ExpenseItem, getExpenseTotalAmount, downloadByCategorySpreadsheet } from './expenseUtils';

interface ByCategoryExpensesProps {
  submittedExpenses: ExpenseItem[];
  downloadingSpreadsheet: boolean;
  onDownloadSpreadsheet: () => void;
}

export default function ByCategoryExpenses({
  submittedExpenses,
  downloadingSpreadsheet,
  onDownloadSpreadsheet,
}: ByCategoryExpensesProps) {
  const navigate = useNavigate();

  // State
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('category');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [colMenuAnchor, setColMenuAnchor] = useState<HTMLElement | null>(null);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(SEC1_COLUMNS.map(c => c.key))
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Helper functions
  const col = (k: string) => visibleCols.has(k);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(p => p === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Data processing
  const groupMap: Record<string, ExpenseItem[]> = {};
  submittedExpenses.forEach(expense => {
    const ai = (expense as any).ApprovedItem || (expense as any).approvedItem || {};
    const ar = ai.aproval_request || {};
    const ie = ai.item_estimate || {};
    const category = ie.item || ar.title || (expense.isExceptionRequest ? 'Exception' : 'Uncategorized');
    if (!groupMap[category]) {
      groupMap[category] = [];
    }
    groupMap[category].push(expense);
  });

  const groupedRows = Object.entries(groupMap).map(([category, expenses]) => {
    const totalSpent = expenses.reduce((sum, e) => sum + getExpenseTotalAmount(e), 0);
    const firstExpenseAI = (expenses[0] as any).ApprovedItem || {};
    const totalApproved = firstExpenseAI.totalPrice || 0;
    const totalRemaining = firstExpenseAI.remianingAmount ?? firstExpenseAI.remainingAmount ?? 0;
    return {
      category,
      expenses,
      totalSpent,
      totalApproved,
      totalRemaining,
      count: expenses.length,
    };
  });

  // Filter
  const filtered = groupedRows.filter(g => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return g.category.toLowerCase().includes(q);
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const d = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'category': return d * a.category.localeCompare(b.category);
      case 'count': return d * (a.count - b.count);
      case 'approvedBudget': return d * (a.totalApproved - b.totalApproved);
      case 'spent': return d * (a.totalSpent - b.totalSpent);
      case 'remaining': return d * (a.totalRemaining - b.totalRemaining);
      default: return 0;
    }
  });

  if (submittedExpenses.length === 0) {
    return <Typography color="textSecondary">No expenses submitted yet.</Typography>;
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, backgroundColor: '#fafafa', borderBottom: '1px solid #ebebeb', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Filter by category…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ minWidth: 260, '& .MuiInputBase-input': { fontSize: '0.82rem', py: '6px' } }}
        />
        <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>
          {sorted.length} / {groupedRows.length} category{groupedRows.length !== 1 ? 's' : ''}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={e => setColMenuAnchor(e.currentTarget)}
            sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: '#d0d0d0', color: '#555', minWidth: 0, px: 1.5 }}
          >
            ⊞ Columns
          </Button>
          <Menu
            anchorEl={colMenuAnchor}
            open={Boolean(colMenuAnchor)}
            onClose={() => setColMenuAnchor(null)}
            PaperProps={{ sx: { minWidth: 190, px: 0.5, py: 0.5 } }}
          >
            {SEC1_COLUMNS.map(c => (
              <MenuItem key={c.key} dense disableRipple sx={{ py: 0.25, px: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={visibleCols.has(c.key)}
                      onChange={() => setVisibleCols(prev => {
                        const next = new Set(prev);
                        next.has(c.key) ? next.delete(c.key) : next.add(c.key);
                        return next;
                      })}
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.8rem' }}>{c.label}</Typography>}
                  sx={{ m: 0 }}
                />
              </MenuItem>
            ))}
          </Menu>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => downloadByCategorySpreadsheet(submittedExpenses)}
            disabled={downloadingSpreadsheet || submittedExpenses.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: '#388e3c', color: '#388e3c', '&:hover': { backgroundColor: '#f1f8f1', borderColor: '#2e7d32' } }}
          >
            {downloadingSpreadsheet ? 'Downloading…' : 'Download Spreadsheet'}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: 40, backgroundColor: '#f5f5f5' }}>►</TableCell>
              {col('category') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 160, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'category'} direction={sortField === 'category' ? sortDir : 'asc'} onClick={() => handleSort('category')}>Category</TableSortLabel>
                </TableCell>
              )}
              {col('count') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 80, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'count'} direction={sortField === 'count' ? sortDir : 'asc'} onClick={() => handleSort('count')}>Count</TableSortLabel>
                </TableCell>
              )}
              {col('approvedBudget') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 110, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'approvedBudget'} direction={sortField === 'approvedBudget' ? sortDir : 'asc'} onClick={() => handleSort('approvedBudget')}>Approved Budget</TableSortLabel>
                </TableCell>
              )}
              {col('spent') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 90, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'spent'} direction={sortField === 'spent' ? sortDir : 'asc'} onClick={() => handleSort('spent')}>Total Spent</TableSortLabel>
                </TableCell>
              )}
              {col('remaining') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 105, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'remaining'} direction={sortField === 'remaining' ? sortDir : 'asc'} onClick={() => handleSort('remaining')}>Remaining</TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((group, groupIdx) => {
              const isExpanded = expandedGroups.has(group.category);
              // Alternate colors per category group
              const groupColors = [
                { border: '#1976d2', bg: '#e3f2fd', headerBg: '#e8eaf6', hover: '#bbdefb', headerHover: '#c5cae9' },
                { border: '#2e7d32', bg: '#e8f5e9', headerBg: '#e8f5e9', hover: '#c8e6c9', headerHover: '#a5d6a7' },
                { border: '#7b1fa2', bg: '#f3e5f5', headerBg: '#f3e5f5', hover: '#e1bee7', headerHover: '#ce93d8' },
                { border: '#e65100', bg: '#fff3e0', headerBg: '#fff3e0', hover: '#ffe0b2', headerHover: '#ffcc80' },
                { border: '#00838f', bg: '#e0f7fa', headerBg: '#e0f7fa', hover: '#b2ebf2', headerHover: '#80deea' },
              ];
              const gc = groupColors[groupIdx % groupColors.length];
              return (
                <React.Fragment key={group.category}>
                  {/* Group Header Row */}
                  <TableRow
                    hover
                    onClick={() => {
                      const newSet = new Set(expandedGroups);
                      newSet.has(group.category) ? newSet.delete(group.category) : newSet.add(group.category);
                      setExpandedGroups(newSet);
                    }}
                    sx={{ cursor: 'pointer', backgroundColor: gc.headerBg, '&:hover': { backgroundColor: gc.headerHover } }}
                  >
                    <TableCell sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, borderLeft: isExpanded ? `3px solid ${gc.border}` : 'none', borderTop: isExpanded ? `2px solid ${gc.border}` : 'none', borderBottom: isExpanded ? `2px solid ${gc.border}` : undefined }}>
                      {isExpanded ? '▼' : '►'}
                    </TableCell>
                    {col('category') && (
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a1a2e', borderTop: isExpanded ? `2px solid ${gc.border}` : 'none', borderBottom: isExpanded ? `2px solid ${gc.border}` : undefined }}>
                        {group.category}
                      </TableCell>
                    )}
                    {col('count') && (
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, borderTop: isExpanded ? `2px solid ${gc.border}` : 'none', borderBottom: isExpanded ? `2px solid ${gc.border}` : undefined }}>
                        {group.count}
                      </TableCell>
                    )}
                    {col('approvedBudget') && (
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, borderTop: isExpanded ? `2px solid ${gc.border}` : 'none', borderBottom: isExpanded ? `2px solid ${gc.border}` : undefined }}>
                        ₹{group.totalApproved.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </TableCell>
                    )}
                    {col('spent') && (
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1976d2', borderTop: isExpanded ? `2px solid ${gc.border}` : 'none', borderBottom: isExpanded ? `2px solid ${gc.border}` : undefined }}>
                        ₹{group.totalSpent.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </TableCell>
                    )}
                    {col('remaining') && (
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: group.totalRemaining < 0 ? '#d32f2f' : '#388e3c', borderTop: isExpanded ? `2px solid ${gc.border}` : 'none', borderRight: isExpanded ? `2px solid ${gc.border}` : 'none', borderBottom: isExpanded ? `2px solid ${gc.border}` : undefined }}>
                        ₹{group.totalRemaining.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </TableCell>
                    )}
                  </TableRow>

                  {/* Expanded Details - Individual Expenses */}
                  {isExpanded && group.expenses.map((expense, expIdx) => {
                    const borderColor = gc.border;
                    const bgColor = gc.bg;
                    const hoverBg = gc.hover;
                    const isFirstItem = expIdx === 0;
                    const isLastItem = expIdx === group.expenses.length - 1;
                    const ai = (expense as any).ApprovedItem || {};
                    const spent = getExpenseTotalAmount(expense);
                    const approvedBudget = ai.totalPrice || 0;
                    const remaining = ai.remianingAmount ?? ai.remainingAmount ?? 0;
                    const docId = (expense as any).documentId || String(expense.id);
                    
                    const expenseDate = expense.createdAt ? new Date(expense.createdAt).toLocaleDateString('en-IN') : '-';
                    const itemsList = (expense.expenseItems || [])
                      .map((it: any) => it.item || '')
                      .filter(Boolean)
                      .join(', ') || 'No items';
                    const exceptionStatus = expense.isExceptionRequest 
                      ? ((expense as any).ExceptionApproval?.status || 'Pending')
                      : 'N/A';
                    
                    return (
                      <React.Fragment key={docId}>
                        <TableRow
                          onClick={() => navigate(`/expense/${docId}`)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: bgColor,
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.75rem', color: '#bbb', borderTop: isFirstItem ? 'none' : `1px dotted ${borderColor}`, borderLeft: `3px solid ${borderColor}`, borderBottom: 'none' }}>└</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#555', pl: 2, borderTop: isFirstItem ? 'none' : `1px dotted ${borderColor}`, borderBottom: 'none' }}>
                            {expense.title || '(Untitled)'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#aaa', borderTop: isFirstItem ? 'none' : `1px dotted ${borderColor}`, borderBottom: 'none' }}>
                            {expense.expenseItems?.length || 0}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', borderTop: isFirstItem ? 'none' : `1px dotted ${borderColor}`, borderBottom: 'none' }}>
                            ₹{approvedBudget.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1976d2', borderTop: isFirstItem ? 'none' : `1px dotted ${borderColor}`, borderBottom: 'none' }}>
                            ₹{spent.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 500, color: remaining < 0 ? '#d32f2f' : '#388e3c', borderTop: isFirstItem ? 'none' : `1px dotted ${borderColor}`, borderRight: `2px solid ${borderColor}`, borderBottom: 'none' }}>
                            ₹{remaining.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                        
                        <TableRow
                          onClick={() => navigate(`/expense/${docId}`)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: bgColor,
                          }}
                        >
                          <TableCell colSpan={6} sx={{ fontSize: '0.7rem', color: '#555', p: 1.5, pl: 4, pt: 0, borderBottom: isLastItem ? `2px solid ${borderColor}` : 'none', borderLeft: `3px solid ${borderColor}`, borderRight: `2px solid ${borderColor}` }}>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              <span><strong>Date:</strong> {expenseDate}</span>
                              <span><strong>Items:</strong> {itemsList}</span>
                              <span>
                                <strong>Exception:</strong> 
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '3px',
                                    backgroundColor:
                                      exceptionStatus === 'Approved' ? '#c8e6c9' :
                                      exceptionStatus === 'Rejected' ? '#ffcdd2' :
                                      exceptionStatus === 'Pending' ? '#fff9c4' :
                                      '#f5f5f5',
                                    color:
                                      exceptionStatus === 'Approved' ? '#2e7d32' :
                                      exceptionStatus === 'Rejected' ? '#c62828' :
                                      exceptionStatus === 'Pending' ? '#f57f17' :
                                      '#666',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                  }}
                                >
                                  {exceptionStatus}
                                </Box>
                              </span>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
          {/* Totals Footer */}
          <TableHead>
            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} />
              {col('category') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total</TableCell>
              )}
              {col('count') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  {sorted.reduce((sum, g) => sum + g.count, 0)}
                </TableCell>
              )}
              {col('approvedBudget') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  ₹{sorted.reduce((sum, g) => sum + g.totalApproved, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </TableCell>
              )}
              {col('spent') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1976d2' }}>
                  ₹{sorted.reduce((sum, g) => sum + g.totalSpent, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </TableCell>
              )}
              {col('remaining') && (() => {
                const totalRemaining = sorted.reduce((sum, g) => sum + g.totalRemaining, 0);
                return (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: totalRemaining < 0 ? '#d32f2f' : '#388e3c' }}>
                    ₹{totalRemaining.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </TableCell>
                );
              })()}
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>
    </Paper>
  );
}
